/**
 * ChatIntentAnalyzer - Système d'analyse complète et contextuelle pour le chat
 * 
 * VERSION OPTIMISÉE - Analyse unifiée qui combine :
 * - Intention (type de requête, outils suggérés)
 * - Progression (avancements détectés)
 * - Contexte (pertinence, répétitions, mémoire)
 * - Recommandations (prochaines actions suggérées)
 * 
 * Utilise GPT-4o-mini pour une analyse rapide et économique (~$0.0015/requête)
 */

import { askGPT } from '../components/OpenAIService';

class ChatIntentAnalyzer {
  constructor() {
    this.model = 'gpt-4o-mini'; // Rapide et économique pour l'analyse
    this.maxRetries = 2; // Nombre de tentatives en cas d'erreur
  }

  /**
   * NOUVELLE MÉTHODE PRINCIPALE - Analyse complète et contextuelle
   * Remplace les analyses séparées d'intention et de progression
   * @param {string} userMessage - Message de l'utilisateur
   * @param {Object} context - Contexte complet de la conversation
   * @returns {Promise<Object>} Analyse complète
   */
  async analyzeComprehensive(userMessage, context) {
    let lastError = null;
    
    // Essayer plusieurs fois en cas d'erreur
    for (let attempt = 0; attempt < this.maxRetries; attempt++) {
      try {
        console.log(`🔍 Analyse tentative ${attempt + 1}/${this.maxRetries}`);
        
        const systemPrompt = this.buildComprehensivePrompt(context);
        const userPrompt = this.buildComprehensiveUserPrompt(userMessage, context);

        const prompt = `${systemPrompt}\n\n${userPrompt}`;
        const response = await askGPT(prompt, {
          temperature: 0.5, // ✨ Augmenté pour plus de flexibilité
          max_tokens: 1000, // ✨ Augmenté pour réponses complètes
          model: this.model
        });

        console.log('📥 Réponse reçue, parsing...');

        // Meilleur nettoyage de la réponse JSON
        let cleanResponse = response.trim();
        cleanResponse = cleanResponse.replace(/```json\s*/g, '').replace(/```\s*/g, '');
        
        // Extraire uniquement le JSON
        const firstBrace = cleanResponse.indexOf('{');
        const lastBrace = cleanResponse.lastIndexOf('}');
        if (firstBrace >= 0 && lastBrace > firstBrace) {
          cleanResponse = cleanResponse.substring(firstBrace, lastBrace + 1);
        }
        
        const result = JSON.parse(cleanResponse);
        
        // Valider les champs obligatoires
        if (!result.intent_type || !result.hasOwnProperty('avancement_detecte')) {
          throw new Error('Champs obligatoires manquants');
        }
        
        console.log('✅ Analyse réussie');
      
        return {
          // Intention
          intent: {
            type: result.intent_type || 'conversation',
            confidence: result.intent_confidence || 0.5,
            suggestedTools: result.suggested_tools || ['gemini_flash'],
            reasoning: result.intent_reasoning || 'Analyse par défaut'
          },
          
          // Progression
          progress: {
            avancement_detecte: result.avancement_detecte || false,
            type: result.avancement_type || null,
            contenu: result.avancement_contenu || null,
            etape_concernee: result.etape_concernee || 'aucune',
            etape_id: result.etape_id || null,
            nouvelle_etape_suggeree: result.nouvelle_etape_suggeree || null,
            contribution_objectif: result.contribution_objectif || 0,
            sections_notes: result.sections_notes || []
          },
          
          // Contexte et mémoire
          context: {
            relevantToObjective: result.relevant_to_objective !== false,
            relevantToStep: result.relevant_to_step !== false,
            needsClarification: result.needs_clarification || false,
            clarificationQuestion: result.clarification_question || null
          },
          
          // Recommandations
          recommendations: {
            shouldUpdateNotes: result.should_update_notes || false,
            shouldChangeStep: result.should_change_step || false,
            shouldCreateStep: result.should_create_step || false,
            suggestedNextQuestions: result.suggested_next_questions || []
          },
          
          justification: result.justification || 'Analyse complète',
          timestamp: new Date().toISOString(),
          method: 'comprehensive_analysis',
          attempt: attempt + 1
        };
        
      } catch (error) {
        lastError = error;
        console.error(`❌ Erreur tentative ${attempt + 1}:`, error.message);
        
        if (attempt < this.maxRetries - 1) {
          console.log('🔄 Nouvelle tentative...');
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
    }
    
    // Toutes les tentatives ont échoué
    console.error('❌ Échec complet de l\'analyse:', lastError);
    return this.getFallbackAnalysis(userMessage, context);
  }

  /**
   * Construit le prompt système pour l'analyse complète
   */
  buildComprehensivePrompt(context) {
    const { 
      objective, 
      currentStep, 
      allSteps, 
      selectedSources,
      stepNotes,
      allStepsNotes,
      confirmedAdvancements,
      conversationHistory
    } = context;
    
    return `Tu es l'analyseur intelligent d'Aladinn, un système de chat contextuel avancé.

MISSION : Analyser chaque message utilisateur de manière COMPLÈTE et CONTEXTUELLE.

═══════════════════════════════════════════════════════════════
CONTEXTE UTILISATEUR
═══════════════════════════════════════════════════════════════

📌 OBJECTIF PRINCIPAL
${objective?.titre || 'Non défini'}
${objective?.description ? `Description : ${objective.description}` : ''}

📍 ÉTAPE COURANTE
${currentStep?.titre || 'Aucune'}
${currentStep?.description ? `Description : ${currentStep.description}` : ''}
${currentStep?.objectif ? `Objectif de l'étape : ${currentStep.objectif}` : ''}

📋 PLAN D'ACTION
${allSteps?.length > 0 ? allSteps.map((s, i) => 
  `${i + 1}. ${s.titre}${s.id === currentStep?.id ? ' ← ACTUELLE' : ''}`
).join('\n') : 'Aucun plan défini'}

📚 SOURCES DISPONIBLES
${selectedSources?.length > 0 ? 
  `${selectedSources.length} sources web sélectionnées` : 
  'Aucune source sélectionnée'}

📝 NOTES ACCUMULÉES (Étape courante)
${stepNotes && (stepNotes.decisions || stepNotes.questions || stepNotes.resources || stepNotes.ai_recommendations) ? `
${stepNotes.decisions ? `✅ Décisions prises :
${stepNotes.decisions}
` : ''}
${stepNotes.questions ? `❓ Questions/Défis :
${stepNotes.questions}
` : ''}
${stepNotes.resources ? `📚 Ressources obtenues :
${stepNotes.resources}
` : ''}
${stepNotes.ai_recommendations ? `💡 Recommandations IA :
${stepNotes.ai_recommendations}
` : ''}` : 'Aucune note pour cette étape'}

📚 HISTORIQUE COMPLET DES DÉCISIONS (Toutes les étapes)
${allStepsNotes?.length > 0 ? 
  allStepsNotes.map(stepData => `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📍 ÉTAPE ${stepData.stepOrder}: ${stepData.stepTitle} ${stepData.isCurrentStep ? '← ACTUELLE' : '✅ COMPLÉTÉE'}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${stepData.notes.decisions ? `✅ Décisions prises :
${stepData.notes.decisions}
` : ''}
${stepData.notes.questions ? `❓ Questions/Défis rencontrés :
${stepData.notes.questions}
` : ''}
${stepData.notes.resources ? `📚 Ressources obtenues :
${stepData.notes.resources}
` : ''}
${stepData.notes.ai_recommendations ? `💡 Recommandations IA données :
${stepData.notes.ai_recommendations}
` : ''}
`).join('\n') : 
'Aucune note historique disponible'}

⚠️ RÈGLE CRITIQUE : Utilise cet historique pour :
- Détecter si l'utilisateur mentionne des décisions/choix passés
- Comprendre le contexte global du projet
- Éviter de redemander des informations déjà fournies
- Identifier les dépendances entre étapes
- Reconnaître la progression logique du projet

✅ AVANCEMENTS CONFIRMÉS
${confirmedAdvancements?.length > 0 ? 
  confirmedAdvancements.map(a => `- ${a.content} (${a.category}, ${a.percentage}%)`).join('\n') :
  'Aucun avancement confirmé'}

📊 PROGRESSION ÉTAPE COURANTE
${(() => {
  if (!confirmedAdvancements || confirmedAdvancements.length === 0) {
    return 'Étape non démarrée (0%)';
  }
  
  const stepAdvancements = confirmedAdvancements.filter(
    adv => adv.metadata?.stepId === currentStep?.id
  );
  
  if (stepAdvancements.length === 0) {
    return 'Aucun avancement pour cette étape (0%)';
  }
  
  const totalPercentage = stepAdvancements.reduce((sum, adv) => sum + (adv.percentage || 0), 0);
  const percentage = Math.min(100, totalPercentage);
  const advCount = stepAdvancements.length;
  
  let status = '';
  if (percentage >= 100 || advCount >= 5) {
    status = '✅ ÉTAPE COMPLÈTE';
  } else if (percentage >= 80 || advCount >= 4) {
    status = '🎯 PRESQUE TERMINÉE';
  } else if (percentage >= 50) {
    status = '⚡ EN BONNE VOIE';
  } else {
    status = '🚀 EN COURS';
  }
  
  return `${status}
Complétion : ${percentage}%
Avancements validés : ${advCount}
${percentage < 100 ? `Reste à faire : ${100 - percentage}%` : 'Prêt à passer à l\'étape suivante'}`;
})()}

💬 HISTORIQUE CONVERSATION (10 derniers messages)
${conversationHistory?.slice(-10).map(m => 
  `${m.role === 'user' ? '👤' : '🤖'} ${m.content.substring(0, 150)}${m.content.length > 150 ? '...' : ''}`
).join('\n') || 'Aucun historique'}

═══════════════════════════════════════════════════════════════
TON ANALYSE DOIT DÉTERMINER
═══════════════════════════════════════════════════════════════

1️⃣ INTENTION (Type de requête)
   - web_search : Recherche de nouvelles infos sur le web
   - source_analysis : Analyse des sources sélectionnées
   - knowledge_base : Recherche dans la base de connaissances
   - conversation : Discussion, réflexion, brainstorming
   - clarification_needed : Demande trop vague

2️⃣ PROGRESSION (Avancement détecté ?)
   - Est-ce une ACTION concrète réalisée ?
   - Est-ce une DÉCISION prise ?
   - Est-ce un OBSTACLE surmonté ?
   - Est-ce une RESSOURCE obtenue ?
   - Est-ce une DÉCOUVERTE importante ?
   
   ⚠️ IMPORTANT : Vérifie si c'est NOUVEAU (pas déjà dans les notes)

3️⃣ ÉTAPE CONCERNÉE
   - current : Concerne l'étape courante
   - autre : Concerne une autre étape du plan (précise laquelle)
   - nouvelle : Contribue à l'objectif mais hors plan
   - aucune : Pas lié à l'objectif

4️⃣ RECOMMANDATIONS
   - Faut-il mettre à jour les notes ?
   - Faut-il changer d'étape ?
   - Faut-il créer une nouvelle étape ?
   - Quelles questions suggérer ensuite ?

6️⃣ DÉTECTION DE COMPLÉTION D'ÉTAPE
   ⚠️ RÈGLE CRITIQUE : Si l'étape courante est COMPLÈTE (100% ou 5+ avancements) :
   - should_change_step = true
   - Suggérer de passer à l'étape suivante du plan
   - NE PAS poser de questions sur l'étape actuelle
   - Féliciter l'utilisateur et proposer la suite

═══════════════════════════════════════════════════════════════
FORMAT DE RÉPONSE (JSON UNIQUEMENT)
═══════════════════════════════════════════════════════════════

{
  "intent_type": "web_search|source_analysis|knowledge_base|conversation|clarification_needed",
  "intent_confidence": 0.0-1.0,
  "suggested_tools": ["gemini_search", "perplexity_sonar", "gemini_flash", "knowledge_base"],
  "intent_reasoning": "Pourquoi cette intention (max 150 caractères)",
  
  "avancement_detecte": true|false,
  "avancement_type": "decision|action|obstacle_solved|ressource|discovery|learning|completion",
  "avancement_contenu": "Description courte (max 150 caractères)",
  "etape_concernee": "current|autre|nouvelle|aucune",
  "etape_id": "id de l'étape si autre, sinon null",
  "nouvelle_etape_suggeree": {
    "titre": "titre suggéré",
    "description": "description courte"
  },
  "contribution_objectif": 0-100,
  "sections_notes": ["decisions", "questions", "resources", "recommendations"],
  
  "relevant_to_objective": true|false,
  "relevant_to_step": true|false,
  "needs_clarification": true|false,
  "clarification_question": "Question à poser si besoin",
  
  "should_update_notes": true|false,
  "should_change_step": true|false,
  "should_create_step": true|false,
  "suggested_next_questions": ["Question 1", "Question 2", "Question 3"],
  
  "justification": "Explication complète de ton analyse (max 300 caractères)"
}

═══════════════════════════════════════════════════════════════
RÈGLES CRITIQUES
═══════════════════════════════════════════════════════════════

✅ Vérifie TOUJOURS si l'info est déjà dans les notes
✅ Compare avec l'historique pour détecter les répétitions
✅ Analyse le contexte COMPLET avant de répondre
✅ Si avancement détecté, vérifie quelle étape est concernée
✅ Suggère des questions pertinentes pour progresser
✅ Si hors sujet, demande clarification avec tact

❌ Ne détecte PAS d'avancement pour une simple question
❌ Ne suggère PAS de mettre à jour les notes si déjà présent
❌ Ne recommande PAS de changer d'étape sans raison valide`;
  }

  /**
   * Construit le prompt utilisateur enrichi
   */
  buildComprehensiveUserPrompt(userMessage, context) {
    let prompt = `═══════════════════════════════════════════════════════════════
MESSAGE À ANALYSER
═══════════════════════════════════════════════════════════════

"${userMessage}"

═══════════════════════════════════════════════════════════════
INDICES CONTEXTUELS
═══════════════════════════════════════════════════════════════
`;

    // Ajouter des indices selon le contexte
    if (context.selectedSources?.length > 0) {
      prompt += `\n🔗 L'utilisateur a ${context.selectedSources.length} sources sélectionnées\n`;
    }

    if (context.conversationHistory?.length > 0) {
      const lastUserMessage = context.conversationHistory
        .filter(m => m.role === 'user')
        .slice(-1)[0];
      
      if (lastUserMessage) {
        prompt += `\n💬 Dernier message utilisateur : "${lastUserMessage.content.substring(0, 100)}${lastUserMessage.content.length > 100 ? '...' : ''}"\n`;
      }
    }

    if (context.stepNotes?.decisions) {
      prompt += `\n📝 Des décisions ont déjà été notées pour cette étape\n`;
    }

    if (context.confirmedAdvancements?.length > 0) {
      prompt += `\n✅ ${context.confirmedAdvancements.length} avancements déjà confirmés\n`;
    }

    prompt += `\n═══════════════════════════════════════════════════════════════
ANALYSE MAINTENANT
═══════════════════════════════════════════════════════════════`;

    return prompt;
  }

  /**
   * Analyse de fallback en cas d'erreur
   */
  getFallbackAnalysis(userMessage, context) {
    return {
      intent: {
        type: 'conversation',
        confidence: 0.5,
        suggestedTools: ['gemini_flash'],
        reasoning: 'Analyse par défaut suite à une erreur'
      },
      progress: {
        avancement_detecte: false,
        type: null,
        contenu: null,
        etape_concernee: 'aucune',
        etape_id: null,
        nouvelle_etape_suggeree: null,
        contribution_objectif: 0,
        sections_notes: []
      },
      context: {
        relevantToObjective: true,
        relevantToStep: true,
        needsClarification: false,
        clarificationQuestion: null
      },
      recommendations: {
        shouldUpdateNotes: false,
        shouldChangeStep: false,
        shouldCreateStep: false,
        suggestedNextQuestions: []
      },
      justification: 'Analyse de secours utilisée',
      timestamp: new Date().toISOString(),
      method: 'fallback',
      error: true
    };
  }

  /**
   * Analyse l'intention de l'utilisateur
   * @param {string} userMessage - Message de l'utilisateur
   * @param {Object} context - Contexte de la conversation
   * @returns {Promise<Object>} Résultat de l'analyse
   */
  async analyzeIntent(userMessage, context) {
    try {
      const systemPrompt = this.buildSystemPrompt(context);
      const userPrompt = this.buildUserPrompt(userMessage, context);

      const prompt = `${systemPrompt}\n\n${userPrompt}`;
      const response = await askGPT(prompt, {
        temperature: 0.3,
        max_tokens: 500
      });

      const result = JSON.parse(response);
      
      return {
        type: result.type,
        confidence: result.confidence,
        relevantToObjective: result.relevantToObjective,
        relevantToStep: result.relevantToStep,
        suggestedTools: result.suggestedTools,
        reasoning: result.reasoning,
        needsClarification: result.needsClarification,
        clarificationQuestion: result.clarificationQuestion,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('Erreur lors de l\'analyse d\'intention:', error);
      // Fallback sur une conversation simple en cas d'erreur
      return {
        type: 'conversation',
        confidence: 0.5,
        relevantToObjective: true,
        relevantToStep: true,
        suggestedTools: ['gemini_flash'],
        reasoning: 'Analyse par défaut suite à une erreur',
        needsClarification: false,
        error: error.message
      };
    }
  }

  /**
   * Construit le prompt système pour l'analyse d'intention
   */
  buildSystemPrompt(context) {
    return `Tu es un analyseur d'intention pour un système de chat contextuel intelligent.

Ton rôle est d'analyser les messages utilisateur et de déterminer :
1. Le TYPE de requête parmi :
   - "web_search" : Recherche de nouvelles informations sur le web
   - "source_analysis" : Analyse de sources web spécifiques déjà sélectionnées
   - "knowledge_base" : Recherche dans la base de connaissances de l'utilisateur
   - "conversation" : Conversation générale, réflexion, brainstorming
   - "clarification_needed" : La demande n'est pas assez précise

2. La PERTINENCE avec le contexte :
   - relevantToObjective : La question est-elle liée à l'objectif ?
   - relevantToStep : La question est-elle liée à l'étape en cours ?

3. Les OUTILS recommandés :
   - "gemini_search" : Recherche Google via Gemini (gratuit, rapide)
   - "perplexity_sonar" : Recherche web avec Perplexity (citations, rapide)
   - "perplexity_analysis" : Analyse de sources avec Perplexity
   - "gemini_flash" : Conversation avec Gemini (contexte 1M tokens)
   - "knowledge_base" : Recherche vectorielle dans la base de connaissances

CONTEXTE ACTUEL :
${context.objective ? `Objectif : ${context.objective.titre}` : 'Aucun objectif défini'}
${context.strategy ? `Stratégie : ${context.strategy.titre}` : ''}
${context.actionPlan ? `Plan d'action : ${context.actionPlan.titre}` : ''}
${context.currentStep ? `Étape en cours : ${context.currentStep.titre}` : ''}
${context.selectedSources?.length > 0 ? `Sources sélectionnées : ${context.selectedSources.length} sources disponibles` : ''}

RÈGLES D'ANALYSE :
- Si la question mentionne "recherche", "trouve", "cherche" → web_search
- Si la question mentionne "analyse ces sources", "que disent ces articles" → source_analysis
- Si la question mentionne "dans mes documents", "dans ma base" → knowledge_base
- Si la question est hors sujet par rapport à l'objectif → demander clarification
- Si la question est vague → clarification_needed
- Sinon → conversation

Réponds UNIQUEMENT en JSON avec cette structure :
{
  "type": "web_search|source_analysis|knowledge_base|conversation|clarification_needed",
  "confidence": 0.0-1.0,
  "relevantToObjective": true|false,
  "relevantToStep": true|false,
  "suggestedTools": ["tool1", "tool2"],
  "reasoning": "Explication courte de ton analyse",
  "needsClarification": true|false,
  "clarificationQuestion": "Question à poser si needsClarification=true"
}`;
  }

  /**
   * Construit le prompt utilisateur pour l'analyse
   */
  buildUserPrompt(userMessage, context) {
    let prompt = `Analyse cette question de l'utilisateur :\n"${userMessage}"\n\n`;

    // Ajouter des indices contextuels
    if (context.selectedSources?.length > 0) {
      prompt += `Note : L'utilisateur a ${context.selectedSources.length} sources web sélectionnées.\n`;
    }

    if (context.conversationHistory?.length > 0) {
      const recentMessages = context.conversationHistory.slice(-3);
      prompt += `\nHistorique récent :\n`;
      recentMessages.forEach(msg => {
        prompt += `- ${msg.role}: ${msg.content.substring(0, 100)}...\n`;
      });
    }

    return prompt;
  }

  /**
   * Détermine si une question nécessite une recherche web
   */
  requiresWebSearch(userMessage) {
    const webSearchKeywords = [
      'recherche', 'trouve', 'cherche', 'quoi de neuf', 'actualité',
      'dernières informations', 'récent', 'nouveau', 'tendance',
      'statistiques', 'données', 'étude', 'rapport'
    ];

    const messageLower = userMessage.toLowerCase();
    return webSearchKeywords.some(keyword => messageLower.includes(keyword));
  }

  /**
   * Détermine si une question concerne les sources sélectionnées
   */
  requiresSourceAnalysis(userMessage, context) {
    if (!context.selectedSources || context.selectedSources.length === 0) {
      return false;
    }

    const sourceKeywords = [
      'ces sources', 'ces articles', 'ces documents', 'ces liens',
      'analyse', 'synthèse', 'résume', 'compare', 'que disent'
    ];

    const messageLower = userMessage.toLowerCase();
    return sourceKeywords.some(keyword => messageLower.includes(keyword));
  }

  /**
   * Détermine si une question concerne la base de connaissances
   */
  requiresKnowledgeBase(userMessage) {
    const kbKeywords = [
      'mes documents', 'ma base', 'mes notes', 'mes fichiers',
      'j\'ai déjà', 'dans mes ressources', 'mes connaissances'
    ];

    const messageLower = userMessage.toLowerCase();
    return kbKeywords.some(keyword => messageLower.includes(keyword));
  }

  /**
   * Vérifie la pertinence avec l'objectif
   */
  isRelevantToObjective(userMessage, objective) {
    if (!objective || !objective.titre) {
      return true; // Pas d'objectif défini, on accepte tout
    }

    // Analyse simple basée sur les mots-clés
    const objectiveKeywords = objective.titre.toLowerCase().split(' ');
    const messageLower = userMessage.toLowerCase();

    const matchCount = objectiveKeywords.filter(keyword => 
      keyword.length > 3 && messageLower.includes(keyword)
    ).length;

    return matchCount > 0 || messageLower.length < 50; // Questions courtes acceptées
  }

  /**
   * Analyse rapide sans appel API (fallback)
   */
  quickAnalyze(userMessage, context) {
    let type = 'conversation';
    const suggestedTools = ['gemini_flash'];

    if (this.requiresWebSearch(userMessage)) {
      type = 'web_search';
      suggestedTools.unshift('gemini_search', 'perplexity_sonar');
    } else if (this.requiresSourceAnalysis(userMessage, context)) {
      type = 'source_analysis';
      suggestedTools.unshift('perplexity_sonar');
    } else if (this.requiresKnowledgeBase(userMessage)) {
      type = 'knowledge_base';
      suggestedTools.unshift('knowledge_base');
    }

    return {
      type,
      confidence: 0.7,
      relevantToObjective: this.isRelevantToObjective(userMessage, context.objective),
      relevantToStep: true,
      suggestedTools,
      reasoning: 'Analyse rapide basée sur les mots-clés',
      needsClarification: false,
      method: 'quick_analysis'
    };
  }
}

export default ChatIntentAnalyzer;
